import type { TenderCase, TenderRun } from "../types"
import type { TenderCaseListItemApi } from "@/api/tender-api"

export type DerivedStatus =
  | "setup"
  | "analyserar"
  | "analyserar_uploading"
  | "analyserar_indexing"
  | "analyserar_extracting"
  | "analyserar_cv_classifying"
  | "analyserar_cv_matching"
  | "extrahering"
  // Phase 1 complete, ready to hand off (extraction done, not yet handed off).
  | "klar_overlamning"
  | "cv_matchning"
  | "anbudsgranskning"
  | "go"
  | "no_go"

export const STATUS_CONFIG: Record<DerivedStatus, { labelKey: string; className: string }> = {
  setup: {
    labelKey: "tender.status.derived.setup",
    className: "bg-muted/60 text-muted-foreground border-border",
  },
  analyserar: {
    labelKey: "tender.status.derived.analyserar",
    className: "bg-accent/50 text-accent-foreground border-accent",
  },
  analyserar_uploading: {
    labelKey: "tender.status.derived.analyserar_uploading",
    className: "bg-accent/50 text-accent-foreground border-accent",
  },
  analyserar_indexing: {
    labelKey: "tender.status.derived.analyserar_indexing",
    className: "bg-accent/50 text-accent-foreground border-accent",
  },
  analyserar_extracting: {
    labelKey: "tender.status.derived.analyserar_extracting",
    className: "bg-accent/50 text-accent-foreground border-accent",
  },
  analyserar_cv_classifying: {
    labelKey: "tender.status.derived.analyserar_cv_classifying",
    className: "bg-accent/50 text-accent-foreground border-accent",
  },
  analyserar_cv_matching: {
    labelKey: "tender.status.derived.analyserar_cv_matching",
    className: "bg-accent/50 text-accent-foreground border-accent",
  },
  extrahering: {
    labelKey: "tender.status.derived.extrahering",
    className: "bg-accent/50 text-accent-foreground border-accent",
  },
  klar_overlamning: {
    labelKey: "tender.status.derived.klar_overlamning",
    className: "bg-warning/10 text-warning-foreground border-warning/30",
  },
  cv_matchning: {
    labelKey: "tender.status.derived.cv_matchning",
    className: "bg-accent/50 text-accent-foreground border-accent",
  },
  anbudsgranskning: {
    labelKey: "tender.status.derived.anbudsgranskning",
    className: "bg-accent/50 text-accent-foreground border-accent",
  },
  go: {
    labelKey: "tender.status.derived.go",
    className: "bg-success/10 text-success border-success/30",
  },
  no_go: {
    labelKey: "tender.status.derived.no_go",
    className: "bg-destructive/10 text-destructive border-destructive",
  },
}

const ACTIVE_AUTO_PIPELINE_STATUSES = new Set([
  "uploading",
  "indexing",
  "extracting",
  "cv_classifying",
  "cv_matching",
])

export function isActiveAutoPipelineStatus(status: string | null | undefined): boolean {
  return status != null && ACTIVE_AUTO_PIPELINE_STATUSES.has(status)
}

export function isProcessingStepStatus(status: string | null | undefined): boolean {
  return status === "processing"
}

function isCompletedExtractionStatus(status: string | null | undefined): boolean {
  return status === "completed"
}

function isDisplayableCvMatchStatus(status: string | null | undefined): boolean {
  return status === "classified" || status === "completed"
}

function isCompletedBidReviewStatus(status: string | null | undefined): boolean {
  return status === "completed"
}

export function deriveRunStatus(
  run: Pick<
    TenderRun,
    "autoPipelineStatus" | "extractionStatus" | "cvMatchStatus" | "bidReviewStatus"
  >,
  caseStatus: TenderCase["status"],
  // The handoff splits the workflow into two phases. Before handoff, "extraction
  // done" means phase 1 is complete and the case is *ready to hand off*; after
  // handoff it advances to the recipient's first phase-2 step (CV matching).
  opts: { isBidReviewStale?: boolean; handedOff?: boolean } = {}
): DerivedStatus {
  const { isBidReviewStale = false, handedOff = false } = opts
  if (caseStatus === "go") return "go"
  if (caseStatus === "no_go") return "no_go"
  if (isActiveAutoPipelineStatus(run.autoPipelineStatus)) {
    if (run.autoPipelineStatus === "uploading") return "analyserar_uploading"
    if (run.autoPipelineStatus === "indexing") return "analyserar_indexing"
    if (run.autoPipelineStatus === "extracting") return "analyserar_extracting"
    if (run.autoPipelineStatus === "cv_classifying") return "analyserar_cv_classifying"
    if (run.autoPipelineStatus === "cv_matching") return "analyserar_cv_matching"
    return "analyserar"
  }
  if (isProcessingStepStatus(run.bidReviewStatus)) return "analyserar"
  if (isProcessingStepStatus(run.cvMatchStatus)) return "analyserar"
  if (isProcessingStepStatus(run.extractionStatus)) return "analyserar"
  if (isCompletedBidReviewStatus(run.bidReviewStatus) && !isBidReviewStale)
    return "anbudsgranskning"
  if (isDisplayableCvMatchStatus(run.cvMatchStatus)) return "cv_matchning"
  // A category retry that ended partial after later steps completed must not
  // hide those steps; only a run that never got past extraction shows it.
  if (run.extractionStatus === "partial") return "extrahering"
  if (isCompletedExtractionStatus(run.extractionStatus)) {
    // Handed off → the case is now in phase 2; show CV matching as the current
    // (active) step. Not handed off → phase 1 done, ready to hand off.
    return handedOff ? "cv_matchning" : "klar_overlamning"
  }
  return "setup"
}

/**
 * Derive the display status for a landing-page list item.
 * Shows the furthest live step that has been started on the active run.
 */
export function deriveListItemStatus(item: TenderCaseListItemApi): DerivedStatus {
  return deriveRunStatus(item, item.status, { handedOff: item.handedOffAt != null })
}

/**
 * A case is "in preparation" (phase 1) only while it hasn't progressed past
 * extraction: no CV-matching or bid-review has started, no Go/No-Go decision has
 * been made, and it hasn't been handed off. Everything past extraction belongs to
 * the post-handoff phase — whether or not the explicit hand-off action was taken
 * (a case can be carried forward by the same person without a formal handoff).
 */
export function isInPreparation(
  item: Pick<TenderCaseListItemApi, "status" | "cvMatchStatus" | "bidReviewStatus" | "handedOffAt">
): boolean {
  return (
    item.handedOffAt == null &&
    item.cvMatchStatus == null &&
    item.bidReviewStatus == null &&
    item.status !== "go" &&
    item.status !== "no_go"
  )
}

/**
 * Derive the display status for the case detail header from a full TenderCase.
 * Stale bid review (where docs changed after the review) skips anbudsgranskning
 * and shows the next furthest step instead.
 */
export function deriveCaseStatus(tenderCase: TenderCase, isBidReviewStale = false): DerivedStatus {
  const run = tenderCase.runs.find((r) => r.id === tenderCase.activeRunId) ?? tenderCase.runs.at(0)
  if (!run) return "setup"
  return deriveRunStatus(run, tenderCase.status, {
    isBidReviewStale,
    handedOff: tenderCase.handedOffAt != null,
  })
}
